/**
 * Module dependencies.
 */

import { appRoutes } from 'app/routes';
import { GREEN_BRIGHT, GREEN_DARK, GREEN_MID, Logo, UserBadge, getAuthUser } from './brand';
import { SiteFooter } from './site-footer';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import React, { useMemo } from 'react';

/**
 * Per-card accent palette — adds colour variety while green stays the brand/CTA colour.
 */

const ACCENTS = {
  amber: '#D97706',
  blue: '#2563EB',
  green: '#1B752B',
  rose: '#E11D48',
  teal: '#0D9488',
  violet: '#7C3AED'
};

/**
 * Animated 5-stop green gradient, shared by the headline and CTA block.
 */

const greenSheen = `linear-gradient(90deg, ${GREEN_DARK}, ${GREEN_MID}, ${GREEN_BRIGHT}, ${GREEN_MID}, ${GREEN_DARK})`;

/**
 * Glassy white card gradient (design uses translucent white cards over the tinted bg).
 */

const glass = (a: number, b: number) => `linear-gradient(160deg, rgba(255,255,255,${a}), rgba(255,255,255,${b}))`;

/**
 * Watermark line-icons (custom, one per feature) — drawn faintly behind each card.
 */

const watermarks: Record<string, JSX.Element> = {
  'Bank accounts': (
    <>
      <rect height={'13'} rx={'2.5'} width={'20'} x={'2'} y={'6'} />

      <path d={'M2 11h20'} />

      <path d={'M6 15.5h4'} />
    </>
  ),
  Crypto: (
    <>
      <circle cx={'9'} cy={'9'} r={'6'} />

      <path d={'M14.5 4.3a6 6 0 0 1 0 11.4'} />

      <path d={'M17 6.5a6 6 0 0 1 0 11'} />
    </>
  ),
  Insights: (
    <>
      <path d={'M4 20V4'} />

      <path d={'M4 20h16'} />

      <path d={'M8.5 20v-6'} />

      <path d={'M13 20V9'} />

      <path d={'M17.5 20v-9.5'} />
    </>
  ),
  Notes: (
    <>
      <rect height={'18'} rx={'2.5'} width={'16'} x={'4'} y={'3'} />

      <path d={'M8 8h8'} />

      <path d={'M8 12h8'} />

      <path d={'M8 16h4'} />
    </>
  ),
  'Private & backed up': (
    <>
      <rect height={'10'} rx={'2.5'} width={'15'} x={'4.5'} y={'10.5'} />

      <path d={'M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3'} />

      <path d={'M12 14.5v2.5'} />
    </>
  ),
  Projects: <path d={'M3 7.5A2 2 0 0 1 5 5.5h3.6l1.8 2.4H19a2 2 0 0 1 2 2v7.6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'} />
};

/**
 * Card watermark.
 */

function Watermark({ color, name }: { color: string; name: string }): JSX.Element {
  return (
    <div
      className={'pointer-events-none absolute'}
      style={{ height: 220, left: -44, opacity: 0.16, top: -34, transform: 'rotate(-30deg)', width: 220 }}
    >
      <svg
        fill={'none'}
        height={'220'}
        stroke={color}
        strokeLinecap={'round'}
        strokeLinejoin={'round'}
        strokeWidth={1.35}
        viewBox={'0 0 24 24'}
        width={'220'}
      >
        {watermarks[name]}
      </svg>
    </div>
  );
}

/**
 * Stats (numbered band).
 */

const stats: { accent: string; description: string; index: string; subtitle: string; title: string }[] = [
  {
    accent: ACCENTS.blue,
    description: 'Banks, cards, savings and cash side by side.',
    index: '01',
    subtitle: 'One single dashboard',
    title: 'All accounts'
  },
  {
    accent: ACCENTS.violet,
    description: 'Three worlds of money, one shared timeline.',
    index: '02',
    subtitle: 'Banks, projects, crypto',
    title: '3-in-1'
  },
  {
    accent: ACCENTS.amber,
    description: 'Balances refresh as transactions land.',
    index: '03',
    subtitle: 'Always up to date',
    title: 'Real-time'
  },
  {
    accent: ACCENTS.teal,
    description: 'Encrypted, exportable, never sold.',
    index: '04',
    subtitle: 'Yours and private',
    title: '100%'
  }
];

/**
 * Features.
 */

const features: { accent: string; description: string; title: string }[] = [
  {
    accent: ACCENTS.blue,
    description: 'Connect your banks and let transactions flow in automatically.',
    title: 'Bank accounts'
  },
  {
    accent: ACCENTS.violet,
    description: 'Group income and expenses, budgets, proposals and suppliers.',
    title: 'Projects'
  },
  {
    accent: ACCENTS.amber,
    description: 'Follow providers, trades and every transaction in one ledger.',
    title: 'Crypto'
  },
  {
    accent: ACCENTS.teal,
    description: 'Categorize, budget and read your month with clear charts.',
    title: 'Insights'
  },
  {
    accent: ACCENTS.rose,
    description: 'Rich notes linked to the accounts and projects that matter.',
    title: 'Notes'
  },
  {
    accent: ACCENTS.green,
    description: 'Your data protected, encrypted and always under your control.',
    title: 'Private & backed up'
  }
];

/**
 * Green radial glow.
 */

function Glow({
  blur,
  className,
  size,
  tint
}: {
  blur: number;
  className: string;
  size: number;
  tint: string;
}): JSX.Element {
  return (
    <div
      aria-hidden
      className={`animate-blob-slow pointer-events-none absolute rounded-full ${className}`}
      style={{ backgroundImage: tint, filter: `blur(${blur}px)`, height: size, opacity: 0.95, width: size }}
    />
  );
}

/**
 * Primary gradient button ("Get started").
 */

function GetStartedButton({ onClick, light = false }: { light?: boolean; onClick: () => void }): JSX.Element {
  if (light) {
    return (
      <button
        className={
          'inline-flex h-11 items-center gap-2 rounded-lg bg-[length:200%_auto] bg-left px-6 text-[15px] font-medium text-[#1B752B] transition-[background-position] duration-500 ease-out hover:bg-right'
        }
        onClick={onClick}
        style={{ backgroundImage: 'linear-gradient(120deg, #ffffff, #eafaef, #ffffff)' }}
        type={'button'}
      >
        {'Get started'}

        <ArrowRight className={'h-4 w-4'} />
      </button>
    );
  }

  return (
    <button
      className={
        'inline-flex h-11 items-center gap-2 rounded-lg bg-[length:200%_auto] bg-left px-[22px] text-[15px] font-medium text-white transition-[background-position] duration-500 ease-out hover:bg-right'
      }
      onClick={onClick}
      style={{ backgroundImage: `linear-gradient(120deg, ${GREEN_DARK}, ${GREEN_MID}, ${GREEN_BRIGHT})` }}
      type={'button'}
    >
      {'Get started'}

      <ArrowRight className={'h-4 w-4'} />
    </button>
  );
}

/**
 * Hero visual — floating "contab.app" dashboard preview.
 */

function HeroVisual(): JSX.Element {
  const bars = [46, 60, 40, 72, 52, 88, 66, 96];
  const rows = [
    { amount: '+ € 2,400.00', label: 'Studio invoice · Projects', positive: true },
    { amount: '− € 500.00', label: 'BTC buy · Crypto', positive: false },
    { amount: '− € 860.00', label: 'Rent · Household', positive: false }
  ];

  return (
    <div className={'animate-fade-up mx-auto mt-20 max-w-4xl text-left [animation-delay:320ms]'} style={{ opacity: 0 }}>
      <div
        className={'animate-float rounded-[20px] p-3 backdrop-blur-xl'}
        style={{
          backgroundImage: glass(0.78, 0.5),
          boxShadow:
            '0 50px 100px -40px rgba(10,10,11,0.4), 0 0 0 1px rgba(228,228,231,0.6), inset 0 1px 0 0 rgba(255,255,255,0.9)'
        }}
      >
        {/* Window bar */}
        <div className={'flex items-center gap-2 px-3 pb-3 pt-1'}>
          <span className={'h-2.5 w-2.5 rounded-full bg-neutral-300/70'} />

          <span className={'h-2.5 w-2.5 rounded-full bg-neutral-300/70'} />

          <span className={'h-2.5 w-2.5 rounded-full bg-neutral-300/70'} />

          <span className={'ml-3 text-xs text-neutral-400'}>{'contab.app / overview'}</span>
        </div>

        <div className={'grid gap-4 p-2 sm:grid-cols-2'}>
          {/* Left column: balance + mini cards */}
          <div className={'space-y-4'}>
            <div
              className={'relative overflow-hidden rounded-2xl p-5 text-white'}
              style={{
                backgroundImage: `linear-gradient(140deg, ${GREEN_MID}, ${GREEN_DARK})`,
                boxShadow: '0 20px 40px -18px rgba(27,117,43,0.6)'
              }}
            >
              <div
                className={'pointer-events-none absolute inset-0'}
                style={{
                  backgroundImage: 'radial-gradient(circle at 100% 0%, rgba(255,255,255,0.35), transparent 65%)'
                }}
              />

              <div className={'relative text-[11px] font-medium uppercase tracking-wide text-white/80'}>
                {'Total balance'}
              </div>

              <div className={"relative mt-1 font-['Outfit'] text-3xl font-semibold tracking-tight"}>
                {'€ 48,320.75'}
              </div>

              <div className={'relative mt-2 text-xs text-white/85'}>{'+ 4.2% this month'}</div>
            </div>

            <div className={'grid grid-cols-2 gap-4'}>
              {[
                { label: 'Accounts', value: '6' },
                { label: 'Projects', value: '4' }
              ].map(card => (
                <div
                  className={'rounded-xl border border-white/60 p-4'}
                  key={card.label}
                  style={{ backgroundImage: glass(0.9, 0.6) }}
                >
                  <div className={'text-xs text-neutral-400'}>{card.label}</div>

                  <div className={"mt-1 font-['Outfit'] text-2xl font-semibold text-neutral-900"}>{card.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: chart + transactions */}
          <div className={'space-y-4'}>
            <div className={'rounded-2xl border border-white/60 p-4'} style={{ backgroundImage: glass(0.9, 0.6) }}>
              <div className={'mb-4 flex items-center justify-between'}>
                <span className={'text-sm font-semibold text-neutral-800'}>{'Cash flow'}</span>

                <span className={'text-[11px] text-neutral-400'}>{'Last 8 months'}</span>
              </div>

              <div className={'flex h-24 items-end gap-2'}>
                {bars.map((height, index) => (
                  <div
                    className={'flex-1 rounded-md'}
                    key={index}
                    style={{
                      background:
                        index === bars.length - 1
                          ? `linear-gradient(180deg, ${GREEN_DARK}, rgba(27,117,43,0.3))`
                          : `linear-gradient(180deg, ${GREEN_BRIGHT}, rgba(34,165,68,0.28))`,
                      height: `${height}%`
                    }}
                  />
                ))}
              </div>
            </div>

            <div className={'rounded-2xl border border-white/60 p-2'} style={{ backgroundImage: glass(0.9, 0.6) }}>
              {rows.map(row => (
                <div className={'flex items-center justify-between px-3 py-2.5'} key={row.label}>
                  <span className={'text-[13px] text-neutral-600'}>{row.label}</span>

                  <span className={'text-[13px] font-medium'} style={{ color: row.positive ? GREEN_MID : '#111827' }}>
                    {row.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Export `Landing` component.
 */

export function Landing(): JSX.Element {
  const navigate = useNavigate();
  const authUser = useMemo(() => getAuthUser(), []);
  const primaryTarget = authUser ? appRoutes.dashboard.home.base : appRoutes.login;
  const goPrimary = () => navigate(primaryTarget);
  const goLogin = () => navigate(appRoutes.login);
  const goDashboard = () => navigate(appRoutes.dashboard.home.base);

  return (
    <div
      className={"relative isolate min-h-screen overflow-x-hidden font-['Roboto'] text-[#0A0A0B]"}
      style={{ backgroundColor: '#F4F4F6' }}
    >
      {/* Dotted pattern (full page) */}
      <div
        aria-hidden
        className={'pointer-events-none absolute inset-0 -z-10'}
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(10,10,11,0.16) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(90% 45% at 50% 0%, #000 35%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(90% 45% at 50% 0%, #000 35%, transparent 100%)'
        }}
      />

      {/* Nav */}
      <header
        className={'sticky top-0 z-20 border-b'}
        style={{
          backdropFilter: 'blur(18px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.6)',
          backgroundColor: 'rgba(255,255,255,0.62)',
          borderColor: 'rgba(228,228,231,0.7)'
        }}
      >
        <div className={'mx-auto flex h-[72px] w-full max-w-6xl items-center justify-between px-6'}>
          <Logo />

          {authUser ? (
            <UserBadge onClick={goDashboard} user={authUser} />
          ) : (
            <button
              className={
                'rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-800 shadow-sm transition-colors hover:bg-neutral-50'
              }
              onClick={goLogin}
              type={'button'}
            >
              {'Sign in'}
            </button>
          )}
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className={'relative mx-auto w-full max-w-6xl px-6 pb-10 pt-24 text-center sm:pt-28'}>
          {/* Green glows */}
          <Glow
            blur={90}
            className={'-left-40 -top-44 [animation-delay:0s]'}
            size={660}
            tint={'radial-gradient(circle at 40% 40%, rgba(34,165,68,0.34), transparent 70%)'}
          />

          <Glow
            blur={100}
            className={'-right-52 top-72 [animation-delay:6s]'}
            size={700}
            tint={'radial-gradient(circle, rgba(27,117,43,0.26), transparent 70%)'}
          />

          <div className={'relative'}>
            <h1
              className={
                "animate-fade-up mx-auto max-w-4xl font-['Outfit'] text-5xl font-semibold leading-[1.04] tracking-[-0.035em] sm:text-[76px]"
              }
            >
              {'All your money,'}

              <br />

              <span
                className={'animate-gradient-pan bg-clip-text text-transparent'}
                style={{ backgroundImage: greenSheen, backgroundSize: '200% auto' }}
              >
                {'in one clear view.'}
              </span>
            </h1>

            <p
              className={'animate-fade-up mx-auto mt-6 max-w-xl text-lg text-neutral-500 [animation-delay:120ms]'}
              style={{ opacity: 0 }}
            >
              {
                'Contab brings bank accounts, projects, crypto and notes together — so you always know exactly where you stand.'
              }
            </p>

            <div className={'animate-fade-up mt-9 [animation-delay:220ms]'} style={{ opacity: 0 }}>
              <GetStartedButton onClick={goPrimary} />
            </div>

            <HeroVisual />
          </div>
        </section>

        {/* Stats */}
        <section className={'mx-auto w-full max-w-6xl px-6 py-16'}>
          <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'}>
            {stats.map(stat => (
              <div
                className={'relative overflow-hidden rounded-2xl p-6'}
                key={stat.index}
                style={{
                  backgroundImage: glass(0.9, 0.6),
                  boxShadow: '0 16px 40px -26px rgba(10,10,11,0.34), inset 0 1px 0 0 rgba(255,255,255,0.9)'
                }}
              >
                <div className={'relative mb-3 flex items-center gap-2 text-xs font-medium text-neutral-400'}>
                  <span
                    className={'h-1.5 w-1.5 rounded-full'}
                    style={{ background: stat.accent, boxShadow: `0 0 0 3px ${stat.accent}29` }}
                  />

                  {stat.index}
                </div>

                <div
                  className={"relative font-['Outfit'] text-3xl font-semibold tracking-tight"}
                  style={{ color: stat.accent }}
                >
                  {stat.title}
                </div>

                <div className={'relative mt-3 text-sm font-semibold text-neutral-800'}>{stat.subtitle}</div>

                <div className={'relative mt-1 text-sm text-neutral-500'}>{stat.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className={'relative mx-auto w-full max-w-6xl px-6 py-16 sm:py-24'} id={'features'}>
          <Glow
            blur={110}
            className={'left-1/4 top-20 [animation-delay:9s]'}
            size={585}
            tint={'radial-gradient(circle, rgba(34,165,68,0.2), transparent 72%)'}
          />

          <div className={'relative mx-auto max-w-2xl text-center'}>
            <h2 className={"font-['Outfit'] text-4xl font-semibold tracking-[-0.03em]"}>
              {'Everything you track, together'}
            </h2>

            <p className={'mx-auto mt-4 max-w-xl text-neutral-500'}>
              {
                'Every account, project, coin and note lives in the same place, updated as your money moves — so you can stop stitching spreadsheets together and simply read where you stand today.'
              }
            </p>
          </div>

          <div className={'relative mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'}>
            {features.map(feature => (
              <div
                className={
                  'group relative overflow-hidden rounded-2xl p-7 transition-transform duration-300 hover:-translate-y-1'
                }
                key={feature.title}
                style={{
                  backgroundImage: glass(0.82, 0.56),
                  boxShadow: '0 14px 40px -26px rgba(10,10,11,0.3), inset 0 1px 0 0 rgba(255,255,255,0.85)'
                }}
              >
                <Watermark color={feature.accent} name={feature.title} />

                <h3 className={"relative font-['Outfit'] text-xl font-semibold tracking-tight"}>{feature.title}</h3>

                <p className={'relative mt-2 text-[15px] leading-relaxed text-neutral-500'}>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className={'mx-auto w-full max-w-6xl px-6 pb-20'}>
          <div
            className={
              'animate-gradient-pan relative overflow-hidden rounded-3xl px-8 py-20 text-center text-white shadow-[0_40px_80px_-34px_rgba(27,117,43,0.7)]'
            }
            style={{ backgroundImage: greenSheen.replace('90deg', '120deg'), backgroundSize: '200% auto' }}
          >
            {/* Soft light from the top */}
            <div
              className={'pointer-events-none absolute inset-0'}
              style={{
                backgroundImage: 'radial-gradient(120% 90% at 50% -25%, rgba(255,255,255,0.4), transparent 55%)'
              }}
            />

            {/* Financial growth chart motif */}
            <svg
              className={'pointer-events-none absolute inset-x-0 bottom-0 h-2/3 w-full'}
              fill={'none'}
              preserveAspectRatio={'none'}
              viewBox={'0 0 100 42'}
            >
              <defs>
                <linearGradient id={'cta-area'} x1={'0'} x2={'0'} y1={'0'} y2={'1'}>
                  <stop offset={'0%'} stopColor={'#ffffff'} stopOpacity={0.18} />

                  <stop offset={'100%'} stopColor={'#ffffff'} stopOpacity={0} />
                </linearGradient>
              </defs>

              <path
                d={'M0 34 L12 32 L24 34 L36 26 L48 28 L60 19 L72 21 L84 12 L96 7 L100 6 L100 42 L0 42 Z'}
                fill={'url(#cta-area)'}
              />

              <path
                d={'M0 34 L12 32 L24 34 L36 26 L48 28 L60 19 L72 21 L84 12 L96 7 L100 6'}
                stroke={'rgba(255,255,255,0.45)'}
                strokeLinecap={'round'}
                strokeLinejoin={'round'}
                strokeWidth={1.5}
                vectorEffect={'non-scaling-stroke'}
              />
            </svg>

            {/* Glowing value node at the chart peak */}
            <span
              className={'pointer-events-none absolute right-[7%] top-[45%] h-2.5 w-2.5 rounded-full bg-white'}
              style={{ boxShadow: '0 0 0 5px rgba(255,255,255,0.25), 0 0 14px 2px rgba(255,255,255,0.6)' }}
            />

            <h2 className={"relative font-['Outfit'] text-3xl font-semibold tracking-[-0.03em] sm:text-4xl"}>
              {'Start seeing the whole picture'}
            </h2>

            <p className={'relative mx-auto mt-3 max-w-md text-white/85'}>
              {'Set up Contab in minutes and bring every account, project and coin into one clear view.'}
            </p>

            <div className={'relative mt-8'}>
              <GetStartedButton light onClick={goPrimary} />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
